<template>
  <LabelValue
    :label="t('Reviews.product.view.contact.label').toString()"
    :help-tooltip="!secondCTAFenced ? t('Reviews.product.view.detailsEdit.ctaHelp') : undefined"
    :help-link="!secondCTAFenced ? t('Reviews.product.view.detailsEdit.ctaHelpLink') : undefined"
  >
    <FeatureFencingWrapper
      :fenced="firstCTAFenced"
      :label="t('Reviews.product.view.detailsEdit.ctaPrimaryFencingLabel')"
      :help-link="t(`Reviews.${subject}.view.detailsEdit.ctaPrimaryFencingHelpLink`)"
      :help-tooltip="t(`Reviews.${subject}.view.detailsEdit.ctaPrimaryFencingTooltip`)"
      :upgrade-cta="t('Reviews.product.view.detailsEdit.ctaPrimaryFencingUpgradeCta')"
      :upgrade-cta-link="t('Reviews.product.view.detailsEdit.ctaPrimaryFencingUpgradeCtaLink')"
      placement="cta_primary"
      :entity-type="entityType"
      :entity-id="entityId"
    >
      <div class="grid grid-cols-2 gap-6">
        <div>
          <div class="mb-1 text-body-m-bold" :class="{ 'text-disabled': firstCTAFenced }">
            {{ t('Reviews.product.view.contact.primary.label') }}
          </div>
          <div class="mb-4 text-body-s" :class="firstCTAFenced ? 'text-disabled' : 'text-subtle'">
            {{ hint }}
          </div>
          <span data-testid="cta-primary-checkbox">
            <CheckBox v-model="v$.ctaPrimaryEnabled.$model" class="pb-4" :disabled="firstCTAFenced">
              {{ t('Reviews.product.edit.contact.ctaPrimaryEnabled.label') }}
            </CheckBox>
          </span>
        </div>
        <div class="flex flex-col rounded-md bg-surface-elevation-2 p-8">
          <ChipBadge class="mb-4 w-fit" :class="{ 'text-disabled': firstCTAFenced }" :color="firstCTAFenced ? 'white' : 'yellow'" rounded>
            {{ t('Reviews.badge.preview') }}
          </ChipBadge>
          <p v-if="!formData.ctaPrimaryEnabled" class="pb-4 text-body-m-bold text-disabled">
            {{ t('Reviews.product.view.contact.primary.preview.headlineHidden') }}
          </p>
          <SlideTransition>
            <div
              v-if="formData.ctaPrimaryEnabled"
              class="btn-primary-color-s pointer-events-none w-fit"
              data-testid="cta-primary-preview-label"
              :disabled="firstCTAFenced"
            >
              {{ t('Reviews.product.view.contact.primary.preview.label') }}
            </div>
          </SlideTransition>
        </div>
      </div>
    </FeatureFencingWrapper>

    <FeatureFencingWrapper
      :fenced="secondCTAFenced"
      :label="t('Reviews.product.view.detailsEdit.ctaSecondaryFencingLabel')"
      :help-link="t(`Reviews.${subject}.view.detailsEdit.ctaSecondaryFencingHelpLink`)"
      :help-tooltip="t(`Reviews.${subject}.view.detailsEdit.ctaSecondaryFencingTooltip`)"
      :upgrade-cta="t('Reviews.product.view.detailsEdit.ctaSecondaryFencingUpgradeCta')"
      :upgrade-cta-link="t('Reviews.product.view.detailsEdit.ctaSecondaryFencingUpgradeCtaLink')"
      placement="cta_secondary"
      :entity-type="entityType"
      :entity-id="entityId"
    >
      <div v-for="lang in ALLOWED_LOCALES" :key="lang" class="mt-6 mb-6 grid grid-cols-2 gap-6">
        <div>
          <div class="flex justify-between">
            <div class="text-body-m-bold" :class="{ 'text-disabled': secondCTAFenced }">
              {{ t('Reviews.product.view.contact.secondary.label') }}
            </div>
            <ChipBadge
              class="self-center uppercase"
              :class="{ 'text-disabled': secondCTAFenced }"
              :color="secondCTAFenced ? 'white' : 'purple'"
              size="s"
              rounded
              >{{ lang }}</ChipBadge
            >
          </div>
          <VuelidateField :validation="v$.ctaSecondaryLabel[lang]">
            <template #default="{ error, state, countHint }">
              <FieldWrapper
                :for="`cta-secondary-label-${lang}`"
                :label="t('Reviews.product.edit.contact.ctaSecondary.btnLabel.label').toString()"
                :error="error"
                :count-hint="countHint"
              >
                <template #default="{ id }">
                  <InputField
                    :id="id"
                    v-model="v$.ctaSecondaryLabel[lang].$model"
                    :data-testid="`cta-secondary-label-${lang}`"
                    type="text"
                    :state="state"
                    :disabled="secondCTAFenced"
                    :maxlength="BTN_LABEL_MAX_LENGTH"
                    :placeholder="t('Reviews.product.edit.contact.ctaSecondary.btnLabel.placeholder').toString()"
                  />
                </template>
              </FieldWrapper>
            </template>
          </VuelidateField>
          <VuelidateField :validation="v$.ctaSecondaryUrl[lang]">
            <template #default="{ error, state }">
              <FieldWrapper
                :for="`cta-secondary-url-${lang}`"
                :label="t('Reviews.product.edit.contact.ctaSecondary.btnUrl.label').toString()"
                :error="error"
                hint=" "
              >
                <template #default="{ id }">
                  <InputField
                    :id="id"
                    v-model="v$.ctaSecondaryUrl[lang].$model"
                    :data-testid="`cta-secondary-url-${lang}`"
                    type="text"
                    :state="state"
                    :disabled="secondCTAFenced"
                    :placeholder="t('Reviews.product.edit.contact.ctaSecondary.btnUrl.placeholder').toString()"
                  />
                </template>
              </FieldWrapper>
            </template>
          </VuelidateField>
        </div>
        <div class="flex flex-col rounded-md bg-surface-elevation-2 p-8">
          <ChipBadge class="mb-4 w-fit" :class="{ 'text-disabled': secondCTAFenced }" :color="secondCTAFenced ? 'white' : 'yellow'" rounded>
            {{ t('Reviews.badge.preview') }}
          </ChipBadge>
          <div
            class="btn-outline-color-s pointer-events-none w-fit"
            target="_blank"
            :disabled="secondCTAFenced"
            :data-testid="`cta-secondary-btn-preview-${lang}`"
          >
            {{ formData.ctaSecondaryLabel[lang] || t('Reviews.product.view.contact.secondary.preview.label') }}
          </div>
        </div>
      </div>
    </FeatureFencingWrapper>
  </LabelValue>
</template>

<script setup lang="ts">
import { CheckBox, ChipBadge, FieldWrapper, InputField, SlideTransition } from '@ramp106/omrjs-core-ui'
import { useVuelidate } from '@vuelidate/core'
import { computed, reactive, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import FeatureFencingWrapper from '@/components/ReviewsCommon/Fencing/FeatureFencingWrapper.vue'
import type { CtaFormData } from '@/components/ReviewsManagement'
import { ALLOWED_LOCALES } from '@/helpers/translationsHelper'
import { buildTranslationFieldsRules } from '@/helpers/validationHelper'
import { requiredIf, maxLength, minLength, url } from '@/services/validators'
import LabelValue from '@/ui/LabelValue.vue'
import VuelidateField from '@/ui/VuelidateField.vue'

const BTN_LABEL_MIN_LENGTH = 3
const BTN_LABEL_MAX_LENGTH = 20
const props = withDefaults(
  defineProps<{
    data: {
      ctaSecondaryLabelTranslationsMap: Record<string, string>
      ctaSecondaryUrlTranslationsMap: Record<string, string>
      configFlags: {
        ctaPrimaryActive: boolean
        ctaPrimaryEditable: boolean
        ctaSecondaryEditable: boolean
      }
    }
    hint: string
    entityId: string
    subject?: 'product' | 'agency'
  }>(),
  {
    subject: 'product',
  },
)

const emit = defineEmits(['change'])

const entityType = computed(() => (props.subject === 'agency' ? 'service' : 'tool'))

const firstCTAFenced = computed(() => {
  return !props.data.configFlags.ctaPrimaryEditable
})

const secondCTAFenced = computed(() => {
  return !props.data.configFlags.ctaSecondaryEditable
})

const { t } = useI18n()

const formData = reactive<CtaFormData>({
  ctaPrimaryEnabled: props.data.configFlags.ctaPrimaryActive,
  ctaSecondaryLabel: { ...props.data.ctaSecondaryLabelTranslationsMap },
  ctaSecondaryUrl: { ...props.data.ctaSecondaryUrlTranslationsMap },
})

const rules = computed(() => ({
  ctaPrimaryEnabled: {},
  ctaSecondaryLabel: {
    ...buildTranslationFieldsRules(
      {
        requiredIf: requiredIf(() => !!formData.ctaSecondaryUrl[ALLOWED_LOCALES[0]]),
        maxLength: maxLength(BTN_LABEL_MAX_LENGTH),
        minLength: minLength(BTN_LABEL_MIN_LENGTH),
      },
      [ALLOWED_LOCALES[0]],
    ),
    ...buildTranslationFieldsRules(
      {
        maxLength: maxLength(BTN_LABEL_MAX_LENGTH),
        minLength: minLength(BTN_LABEL_MIN_LENGTH),
      },
      ALLOWED_LOCALES.slice(1),
    ),
  },
  ctaSecondaryUrl: {
    ...buildTranslationFieldsRules(
      {
        requiredIf: requiredIf(() => !!formData.ctaSecondaryLabel[ALLOWED_LOCALES[0]]),
        url,
      },
      [ALLOWED_LOCALES[0]],
    ),
    ...buildTranslationFieldsRules({ url }, ALLOWED_LOCALES.slice(1)),
  },
}))

const v$ = useVuelidate(rules, formData)

watch(
  () => formData,
  () => {
    emit('change', formData)
  },
  { deep: true },
)
</script>
<style scoped>
@reference '@ramp106/omrjs-config-tailwind/styles/index.css';
.text-disabled :deep(label) {
  @apply text-disabled;
}
</style>
